import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Flag, CheckCircle, XCircle } from "lucide-react";

export function DisputeManagement() {
  const { toast } = useToast();
  const [disputes, setDisputes] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDispute, setSelectedDispute] = useState<any>(null);
  const [resolution, setResolution] = useState("");
  const [newStatus, setNewStatus] = useState("");
  const [resolving, setResolving] = useState(false);

  useEffect(() => {
    fetchDisputes();
  }, []);

  const fetchDisputes = async () => {
    try {
      const { data, error } = await supabase
        .from('disputes')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setDisputes(data || []);
    } catch (err) {
      console.error('Error fetching disputes:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleResolve = async () => {
    if (!selectedDispute || !newStatus) return;
    setResolving(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      const { error } = await supabase
        .from('disputes')
        .update({
          status: newStatus,
          resolution: resolution || null,
          resolved_by: user?.id,
          resolved_at: new Date().toISOString(),
        })
        .eq('id', selectedDispute.id);

      if (error) throw error;

      // Notify buyer
      if (selectedDispute.buyer_id) {
        await supabase.rpc('create_notification', {
          p_user_id: selectedDispute.buyer_id,
          p_title: `Dispute ${newStatus === 'resolved' ? 'Resolved' : 'Rejected'}`,
          p_message: `Your dispute for order #${selectedDispute.order_id?.slice(0, 8).toUpperCase()} has been ${newStatus}. ${resolution || ''}`,
          p_type: 'dispute',
          p_severity: newStatus === 'resolved' ? 'success' : 'info',
          p_link: '/buyer/orders',
        });
      }

      toast({ title: "Dispute Updated", description: `Dispute marked as ${newStatus}` });
      setSelectedDispute(null);
      setResolution("");
      setNewStatus("");
      fetchDisputes();
    } catch (err) {
      console.error('Error resolving dispute:', err);
      toast({ title: "Error", description: "Failed to update dispute", variant: "destructive" });
    } finally {
      setResolving(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'open': return <Badge variant="outline" className="bg-destructive/10 text-destructive">Open</Badge>;
      case 'resolved': return <Badge variant="outline" className="bg-primary/10 text-primary">Resolved</Badge>;
      case 'rejected': return <Badge variant="outline" className="bg-muted text-muted-foreground">Rejected</Badge>;
      default: return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-10 text-center">
          <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
          <p>Loading disputes...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Flag className="h-5 w-5" />
          Dispute Management
        </CardTitle>
        <CardDescription>Review and resolve buyer disputes</CardDescription>
      </CardHeader>
      <CardContent>
        {disputes.length === 0 ? (
          <div className="text-center py-10">
            <CheckCircle className="h-12 w-12 mx-auto mb-3 text-primary opacity-50" />
            <p className="text-muted-foreground">No disputes to review</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Order</TableHead>
                <TableHead>Reason</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {disputes.map((dispute) => (
                <TableRow key={dispute.id}>
                  <TableCell className="font-mono text-sm">
                    {dispute.order_id?.slice(0, 8).toUpperCase()}
                  </TableCell>
                  <TableCell className="capitalize">
                    {dispute.reason?.replace(/_/g, ' ')}
                  </TableCell>
                  <TableCell className="max-w-[200px] truncate text-sm text-muted-foreground">
                    {dispute.description || '—'}
                  </TableCell>
                  <TableCell>{getStatusBadge(dispute.status)}</TableCell>
                  <TableCell className="text-sm">
                    {new Date(dispute.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setSelectedDispute(dispute);
                        setNewStatus('');
                        setResolution(dispute.resolution || '');
                      }}
                    >
                      {dispute.status === 'open' ? 'Review' : 'View'}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>

      <Dialog open={!!selectedDispute} onOpenChange={() => setSelectedDispute(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Dispute Details</DialogTitle>
          </DialogHeader>
          {selectedDispute && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Order</p>
                  <p className="font-mono">{selectedDispute.order_id?.slice(0, 8).toUpperCase()}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Status</p>
                  {getStatusBadge(selectedDispute.status)}
                </div>
                <div>
                  <p className="text-muted-foreground">Reason</p>
                  <p className="capitalize">{selectedDispute.reason?.replace(/_/g, ' ')}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Filed</p>
                  <p>{new Date(selectedDispute.created_at).toLocaleString()}</p>
                </div>
              </div>

              {selectedDispute.description && (
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Buyer Description</p>
                  <p className="text-sm bg-muted p-3 rounded">{selectedDispute.description}</p>
                </div>
              )}

              {selectedDispute.status === 'open' && (
                <>
                  <div>
                    <p className="text-sm font-medium mb-1">Resolution</p>
                    <Select value={newStatus} onValueChange={setNewStatus}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select resolution" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="resolved">Resolve (in buyer's favor)</SelectItem>
                        <SelectItem value="rejected">Reject dispute</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Textarea
                    placeholder="Add resolution notes..."
                    value={resolution}
                    onChange={(e) => setResolution(e.target.value)}
                    rows={3}
                  />
                  <Button
                    onClick={handleResolve}
                    disabled={!newStatus || resolving}
                    className="w-full"
                  >
                    {resolving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                    Submit Resolution
                  </Button>
                </>
              )}

              {selectedDispute.resolution && selectedDispute.status !== 'open' && (
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Resolution Notes</p>
                  <p className="text-sm bg-muted p-3 rounded">{selectedDispute.resolution}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}
